/**
 * Filtra gli allenamenti per un determinato intervallo di tempo
 * @param {Array} workouts - Array di allenamenti
 * @param {string} timeRange - Intervallo di tempo ('week', 'month', 'all')
 * @returns {Array} Allenamenti filtrati
 */
export function filterWorkoutsByTimeRange(workouts, timeRange = 'month') {
  if (!workouts || workouts.length === 0) return [];
  
  if (timeRange === 'all') return workouts;

  const now = new Date();
  const cutoffDate = new Date();

  switch (timeRange) {
    case 'week':
      // Ultimi 7 giorni
      cutoffDate.setDate(now.getDate() - 7);
      break;
    case 'month':
    default:
      // Ultimi 30 giorni
      cutoffDate.setDate(now.getDate() - 30);
      break;
  }

  const filtered = workouts.filter(workout => {
    const workoutDate = new Date(workout.data_allenamento);
    return workoutDate >= cutoffDate;
  });
  
  // Se non ci sono allenamenti nel periodo, espandi la finestra temporale
  if (filtered.length === 0 && workouts.length > 0) {
    return workouts.slice(-Math.min(5, workouts.length));
  }
  
  return filtered;
}

/**
 * Calcola statistiche base degli allenamenti
 * @param {Array} workouts - Array di allenamenti
 * @param {string} timeRange - Intervallo di tempo
 * @param {boolean} isDebugMode - Abilita log di debug
 * @returns {Object} Statistiche calcolate
 */
export function calculateWorkoutStats(workouts, timeRange = 'month', isDebugMode = false) {
  const completedWorkouts = workouts.filter(workout => workout.durata_totale !== null);
  
  // Calcola la durata media degli allenamenti
  const totalDuration = completedWorkouts.reduce((sum, workout) => 
    sum + (parseFloat(workout.durata_totale) || 0), 0);
  const averageWorkoutDuration = completedWorkouts.length > 0 
    ? Math.round(totalDuration / completedWorkouts.length)
    : 0;

  // Calcolo della frequenza
  let workoutFrequency = 0; // Inizializza a 0 invece che undefined
  
  if (timeRange === 'week') {
    // Se è ultima settimana, considera solo gli allenamenti negli ultimi 7 giorni
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const weekWorkouts = completedWorkouts.filter(workout => 
      new Date(workout.data_allenamento) >= sevenDaysAgo
    );
    
    workoutFrequency = weekWorkouts.length;
  } else if (timeRange === 'month') {
    // Per ultimo mese, considera gli allenamenti negli ultimi 30 giorni
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const monthWorkouts = completedWorkouts.filter(workout => 
      new Date(workout.data_allenamento) >= thirtyDaysAgo
    );
    
    workoutFrequency = monthWorkouts.length / 4; // Diviso 4 settimane
  } else {
    // Per "Tutto", calcola la frequenza media settimanale
    if (completedWorkouts.length < 2) {
      workoutFrequency = completedWorkouts.length;
    } else {
      // Calcola l'intervallo tra il primo e l'ultimo allenamento in settimane
      const sortedWorkouts = [...completedWorkouts].sort(
        (a, b) => new Date(a.data_allenamento) - new Date(b.data_allenamento)
      );
      const firstDate = new Date(sortedWorkouts[0].data_allenamento);
      const lastDate = new Date(sortedWorkouts[sortedWorkouts.length - 1].data_allenamento);
      const weeksDiff = Math.max(1, Math.round((lastDate - firstDate) / (7 * 24 * 60 * 60 * 1000)));
      
      workoutFrequency = completedWorkouts.length / weeksDiff;
    }
  }

  // Arrotonda la frequenza a 1 decimale
  workoutFrequency = Math.round(workoutFrequency * 10) / 10;

  return {
    totalWorkouts: completedWorkouts.length,
    totalDuration: totalDuration,
    averageWorkoutDuration,
    workoutFrequency
  };
}

/**
 * Analizza i gruppi muscolari allenati
 * @param {Array} sets - Serie di esercizi
 * @returns {Object} Analisi dei gruppi muscolari
 */
export function analyzeMuscleTrained(sets) {
  // Verifica se sets esiste ed è un array
  if (!sets || !Array.isArray(sets) || sets.length === 0) {
    return {
      muscleGroups: [],
      mostTrainedGroup: null,
      leastTrainedGroup: null
    };
  }

  const muscleGroupMap = {};

  // Utilizza il campo gruppo_muscolare direttamente disponibile nei dati
  sets.forEach(set => {
    // Utilizza direttamente il gruppo muscolare dalla tabella esercizi se disponibile
    const muscleGroup = set.gruppo_muscolare || 'Altro';
    
    if (!muscleGroupMap[muscleGroup]) {
      muscleGroupMap[muscleGroup] = 0;
    }
    
    muscleGroupMap[muscleGroup]++;
  });

  // Converti in array ordinato
  const muscleGroupEntries = Object.entries(muscleGroupMap)
    .map(([group, count]) => ({ group, count }))
    .sort((a, b) => b.count - a.count);

  return {
    muscleGroups: muscleGroupEntries,
    mostTrainedGroup: muscleGroupEntries[0]?.group || null,
    leastTrainedGroup: muscleGroupEntries[muscleGroupEntries.length - 1]?.group || null
  };
}

/**
 * Analizza la distribuzione degli esercizi
 * @param {Array} sets - Serie di esercizi
 * @returns {Object} Analisi della distribuzione degli esercizi
 */
export function analyzeExerciseDistribution(sets) {
  // Se sets è vuoto o non definito, ritorna struttura vuota ma definita
  if (!sets || !Array.isArray(sets) || sets.length === 0) {
    return {
      mostCommonExercises: [],
      leastCommonExercises: [],
      totalExercises: 0
    };
  }
  
  // Crea un conteggio per nome di esercizio
  const exerciseCounts = {};
  sets.forEach(set => {
    const exerciseName = set.esercizio_nome;
    if (!exerciseName) return;
    
    exerciseCounts[exerciseName] = (exerciseCounts[exerciseName] || 0) + 1;
  });
  
  const sortedExercises = Object.entries(exerciseCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  return {
    mostCommonExercises: sortedExercises.slice(0, 5),
    leastCommonExercises: sortedExercises.slice(-5).reverse(),
    totalExercises: sortedExercises.length
  };
}